import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import { useNotify, useRefresh, useRecordContext } from 'react-admin';
import { useState } from 'react';

import { useSubmitData } from '../../../../../hooks/useSubmitData';
import { fetchAdsActionGuards, approveAdsAction } from '../../../../../api';
import { getSelectedIndustryId } from '../../../../../utils/industryUtils';

export const ApproveButton = () => {
  const [open, setOpen] = useState(false);
  const [guards, setGuards] = useState(null);
  const [checking, setChecking] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext();
  const { submit, loading } = useSubmitData(approveAdsAction);

  if (!record) return null;

  const handleOpen = async () => {
    setGuards(null);
    setOpen(true);
    setChecking(true);
    try {
      const result = await fetchAdsActionGuards({ id: record.id, industry_id: getSelectedIndustryId() });
      setGuards(result);
    } catch (error) {
      notify(error.message, { type: 'error' });
      setOpen(false);
    } finally {
      setChecking(false);
    }
  };

  const handleClose = () => {
    if (!loading) setOpen(false);
  };

  const handleApprove = async () => {
    try {
      await submit({ id: record.id, industry_id: getSelectedIndustryId() });
      notify('Action approved', { type: 'info' });
      setOpen(false);
      refresh();
    } catch (error) {
      notify(error.message, { type: 'error' });
      if (error.guards) setGuards({ ok: false, guards: error.guards });
    }
  };

  return (
    <>
      <MuiButton
        onClick={handleOpen}
        variant="contained"
        color="success"
        disabled={record.status !== 'proposed'}
      >
        Approve
      </MuiButton>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Approve action #{record.id}</DialogTitle>
        <DialogContent>
          {checking || !guards ? (
            <CircularProgress size={24} />
          ) : (
            <List dense>
              {guards.guards.map((guard) => (
                <ListItem key={guard.name}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {guard.ok ? <CheckCircle color="success" /> : <Cancel color="error" />}
                  </ListItemIcon>
                  <ListItemText primary={guard.name} secondary={guard.message} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={!guards || !guards.ok || loading}
          >
            Approve
          </MuiButton>
          <MuiButton onClick={handleClose} color="primary" disabled={loading}>Cancel</MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
